import { GlobalStore } from '@roc/client-portal-shared/stores';
import { CreditBackgroundType, downloadDocument, GENERIC_ERROR_MESSAGE, isEmptyObject, isNotBlank } from '@roc/client-portal-shared/utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { ApiResponse } from '@roc/feature-app-core';
import { Option } from '@roc/ui/utils';
import { RequestCreditAuthService } from '../services/requestCreditAuthService';

export interface MessageConfig {
  link: string,
  tpoName: string,
  fromEmail: string,
  uuid: string,
  host: string,
}

export interface DecodedParams {
  lenderId: number,
  originatorId: number,
  borrowerId: number,
  requestToken: string,
}

export interface RequestCreditAuthForm {
  lenderId: number,
  originatorId: number,
  borrowerId: number,
  requestToken: string,
  emailAddress: string,
  phoneNumber: string,
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  ssn: string,
  address: string,
  streetNumber: string,
  streetName: string,
  aptNumber: string,
  city: string,
  state: string,
  zipCode: string,
  runCredit: boolean,
  portalId: number,
  citizenshipStatus: string,
}

export class RequestCreditAuthStore {
  globalStore: GlobalStore;
  requestCreditAuthService: RequestCreditAuthService;

  borrowerOptions: Option[] = [];
  originatorId: number;
  messageConfig: MessageConfig;
  borrowerDetailsVerification: any;
  signature: any;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.requestCreditAuthService = new RequestCreditAuthService();
    makeObservable(this, {
      borrowerOptions: observable,
      messageConfig: observable,
      borrowerDetailsVerification: observable,
      signature: observable,
      findBorrowersByName: flow,
      findBorrowersAndLeadsByName: flow,
      fetchRecordData: action,
      getSignature: computed,
      getMessageConfig: flow,
      sendRequestCreditAuthorizationEmail: flow,
      saveFormInformation: flow,
      saveSignatureAndPullCredit: flow,
      submitCreditBgAuthRequest: flow,
      handleSendEmail: flow,
      sendConfirmCafEmail: flow,
      saveBorrower: flow,
      handleSaveBorrower: flow,
    });
  }

  reset() {
    this.borrowerOptions = [];
    this.originatorId = null;
    this.messageConfig = null;
    this.borrowerDetailsVerification = null;
  }

  staticLinkFromOriginator(obj: Object) {
    return obj && (obj['requestToken'] === null || obj['requestToken'] === undefined);
  }

  *getMessageConfig(runCredit: boolean, borrowerId: number = null, requestToken: string = null, lenderId: number = null) {
    try {
      this.reset();
      const response: ApiResponse = yield this.requestCreditAuthService.getMessageConfig(this.globalStore?.portalConfiguration?.host, runCredit, borrowerId, requestToken, lenderId);
      this.messageConfig = response?.data?.data;
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting config.',
      });
    }
  }

  *sendRequestCreditAuthorizationEmail(toEmail: string, subject: string, bodyContent: string, callback?: () => void) {
    try {
      yield this.requestCreditAuthService.sendRequestCreditAuthEmail({
        host: this.messageConfig?.host || this.globalStore?.portalConfiguration?.host,
        fromEmail: this.messageConfig?.fromEmail,
        toEmail,
        subject,
        bodyContent: bodyContent,
        tpoName: this.messageConfig?.tpoName,
        uuid: this.messageConfig?.uuid,
      });
      callback && callback();
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending email.',
      });
    }
  }

  *findBorrowersByName(name, cancelToken?) {
    try {
      if (name.length >= 3) {
        const response = yield this.requestCreditAuthService.findBorrowersByName(
          name,
          cancelToken
        );
        this.borrowerOptions = response.data.data;
      } else {
        this.borrowerOptions = [];
      }
    } catch (e) {
      console.log(e);
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  *findBorrowersAndLeadsByName(name, cancelToken?) {
    try {
      if (name.length >= 3) {
        const response = yield this.requestCreditAuthService.findBorrowersAndLeadsByName(
          name,
          cancelToken
        );
        this.borrowerOptions = response.data.data;
      } else {
        this.borrowerOptions = [];
      }
    } catch (e) {
      console.log(e);
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  get getSignature() {
    return this.signature
  }

  async fetchRecordData(recordId) {
    try {
      const recordComposedKey = recordId.split('-');
      const type = recordComposedKey[0];
      const id = recordComposedKey[1];
      const response = type === 'lead'
        ? await this.requestCreditAuthService.fetchLeadData(id, '')
        : await this.requestCreditAuthService.fetchBorrowerData(id);

      const recordData = response.data;
      const formData = {
        leadId: recordData.leadId,
        borrowerId: recordData.borrowerId,
        email: type === 'lead' ? recordData.email : recordData.emailAddress,
        phone: type === 'lead' ? recordData.phone : recordData.cellPhone ? recordData.cellPhone : (recordData.homePhone ?? ''),
        name: recordData.firstName + ' ' + recordData.lastName,
        firstName: recordData.firstName,
        lastName: recordData.lastName,
        leadSource: recordData.leadSource,
      };

      return formData;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *saveFormInformation(formData: any, decodedParams: DecodedParams, runCredit: boolean, showNotification = true) {
    try {
      // With a requestToken the backend resolves the borrower and credit request from it,
      // so the ids from the link are only sent for static links (no token).
      const linkParams = isNotBlank(decodedParams?.requestToken)
        ? { requestToken: decodedParams.requestToken }
        : decodedParams;
      const requestCreditAuthForm: RequestCreditAuthForm = {
        ...formData,
        ...linkParams,
        runCredit: runCredit,
        portalId: this.globalStore?.portalConfiguration?.id,
        staticLink: isEmptyObject(decodedParams) || this.staticLinkFromOriginator(decodedParams) ? true : false,
      }
      const response = yield this.requestCreditAuthService.saveFormInformation(requestCreditAuthForm);
      this.borrowerDetailsVerification = { requestToken: response?.data?.data?.requestToken };
      if (showNotification) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Your Information was saved successfully.',
        });
      }
      return true;
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving your information.',
      });
      return false;
    }
  }

  *saveSignatureAndPullCredit(signature, formData: any, pdf, confirmEmailBodyContent: any) {
    try {
      this.signature = signature;
      const data = {
        borrowerDetailsVerification: {
          requestToken: this.borrowerDetailsVerification.requestToken,
        },
        signature: signature,
        userAgent: navigator.userAgent,
        emailAddress: formData?.emailAddress,
      }

      const formDataReq = new FormData();

      formDataReq.append('data', JSON.stringify(data));
      formDataReq.append('file', pdf.get('file'));

      yield this.requestCreditAuthService.submitSignatureCaf(formDataReq);
      yield this.sendConfirmCafEmail(formData?.emailAddress, 'Credit Authorization Successfully Submitted', confirmEmailBodyContent);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Your signature was saved successfully.',
      });
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving your signature.',
      });
    }
  }

  *sendConfirmCafEmail(toEmail: string, subject: string, bodyContent: string, callback?: () => void) {
    try {
      yield this.requestCreditAuthService.sendConfirmCAFEmail({
        host: this.globalStore?.portalConfiguration?.host,
        toEmail,
        subject,
        bodyContent: bodyContent,
        requestToken: this.borrowerDetailsVerification?.requestToken,
      });
      callback && callback();
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending email.',
      });
    }
  }

  *submitCreditBgAuthRequest(runCredit, borrowerId, emailAddress, creditBackgroundCheck) {
    try {
      const response = yield this.requestCreditAuthService.submitCreditBgAuthRequest({
        email: emailAddress,
        runCredit: runCredit,
        type: creditBackgroundCheck ? CreditBackgroundType.ID_VERIFICATION : CreditBackgroundType.BG_ID_VERIFICATION_WITHOUT_NOTIFICATION,
        isConfirmed: true,
        isSendCAFConfirmed: true,
        isDuplicateConfirmed: true,
        isBorrower: true,
        borrowerId: borrowerId,
        runBackground: true,
        requestAuthFlowEnabled: true,
      }, this.messageConfig?.uuid);

      return response.data;
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while submiting request.',
      });
    }
  }

  *handleSaveBorrower(runCredit, borrowerId, firstName, lastName, emailAddress, cellPhone, leadId, leadSource) {
    const currentBorrowerId = yield this.saveBorrower(runCredit, borrowerId, firstName, lastName, emailAddress, cellPhone, leadId, leadSource);
    if (currentBorrowerId == null) {
      return null;
    }
    if (this.messageConfig.uuid == null) {
      yield this.getMessageConfig(runCredit, currentBorrowerId);
    }
    return currentBorrowerId;
  }

  *handleSendEmail(runCredit, borrowerId, emailAddress, subject, emailBodyContent, callback) {
    const requestResponse = yield this.submitCreditBgAuthRequest(runCredit, borrowerId, emailAddress, true);
    if (requestResponse.success && !requestResponse.isCAFSigned && (runCredit || !requestResponse.borrowerHasBasicInformation)) {
      yield this.sendRequestCreditAuthorizationEmail(emailAddress, subject, emailBodyContent, callback);
    } else if (!!callback) {
      callback();
    }
  }

  *saveBorrower(runCredit, borrowerId, firstName, lastName, emailAddress, cellPhone, leadId, leadSource) {
    if (borrowerId == null) {
      try {
        const response = yield this.requestCreditAuthService.saveBorrower({
          firstName,
          lastName,
          emailAddress,
          runCreditOnCreation: runCredit,
          cellPhone,
          leadId,
          leadSource,
        })
        return response?.data?.data?.borrowerId;
      } catch (e) {
        console.log(e);
        const response = e?.error?.response;
        const message = response?.status === 400 && response?.data?.error?.message;
        this.globalStore.notificationStore.showErrorNotification({
          message: message || GENERIC_ERROR_MESSAGE,
        });
        return null;
      }
    }
    return borrowerId;
  }

}
